import React from 'react';
import { Drawer } from '../../CompositeComponents/Drawer/Drawer';
import { Card } from '../../CompositeComponents/Card/Card';
import { Button } from '../../BasicComponents/Button/Button';
import { formatMoney } from '../../../utils/currency';
import type { ParkedBill, ParkBillPanelProps } from './types';

function clockLabel(parkedAt: string): string {
  const d = new Date(parkedAt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function elapsedLabel(parkedAt: string): string {
  const ms = Date.now() - new Date(parkedAt).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
}

// ─── ParkBillPanel ────────────────────────────────────────────────────────────

export function ParkBillPanel({
  isOpen,
  onClose,
  parkedBills,
  onPark,
  onResume,
  onDiscard,
  canPark = true,
  currency = 'INR',
  className,
}: ParkBillPanelProps) {
  const fmt = (paise: number): string => formatMoney(paise, currency);
  return (
    <Drawer open={isOpen} onClose={onClose} title="Parked Bills" position="right">
      <div className={`flex flex-col gap-4 h-full ${className ?? ''}`} role="dialog" aria-label="Parked bills">
        {/* Park current bill */}
        <div>
          <Button variant="secondary" fullWidth onClick={onPark} disabled={!canPark}>
            Park Current Bill
          </Button>
          {!canPark && (
            <p className="mt-1 text-center text-xs text-neutral-400 dark:text-neutral-500">
              Add items to the cart to park a bill
            </p>
          )}
        </div>

        {/* List */}
        {parkedBills.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 text-neutral-400 dark:text-neutral-500 gap-2 py-10">
            <svg className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><path d="M12 8v4m0 4h.01" />
            </svg>
            <p className="text-sm font-medium">No parked bills. All clear!</p>
          </div>
        ) : (
          <div className="space-y-2 overflow-y-auto flex-1">
            {parkedBills.map((bill: ParkedBill) => {
              const elapsed = elapsedLabel(bill.parkedAt);
              return (
                <Card key={bill.id} variant="outlined" padding="sm" aria-label={`Parked bill ${bill.referenceNumber}`}>
                  <div role="listitem" className="flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">
                          #{bill.referenceNumber}
                        </p>
                        {bill.customerName && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                            {bill.customerName}
                          </p>
                        )}
                        {bill.summary && (
                          <p className="text-xs text-neutral-400 dark:text-neutral-500 truncate">
                            {bill.summary}
                          </p>
                        )}
                        {bill.parkedByName && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                            Parked by {bill.parkedByName}
                          </p>
                        )}
                        {bill.localState === 'waiting' && (
                          <span className="mt-1 inline-flex items-center rounded-full bg-warning-100 dark:bg-warning-900/40 px-2 py-0.5 text-[11px] font-medium text-warning-700 dark:text-warning-300">
                            Waiting to upload
                          </span>
                        )}
                        {bill.localState === 'device' && (
                          <span className="mt-1 inline-flex items-center rounded-full bg-neutral-100 dark:bg-neutral-700 px-2 py-0.5 text-[11px] font-medium text-neutral-600 dark:text-neutral-300">
                            This till only
                          </span>
                        )}
                        {bill.uploadError && (
                          <p role="alert" className="mt-1 text-xs text-danger-600 dark:text-danger-400">
                            Could not upload: {bill.uploadError}
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-neutral-800 dark:text-neutral-100">{fmt(bill.total)}</p>
                        <p className="text-xs text-neutral-400 dark:text-neutral-500">{bill.itemCount} item{bill.itemCount !== 1 ? 's' : ''}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-neutral-400 dark:text-neutral-500">
                        {elapsed}
                        {clockLabel(bill.parkedAt) && <span> · {clockLabel(bill.parkedAt)}</span>}
                      </p>
                      <div className="flex gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDiscard(bill.id)}
                          aria-label={`Discard parked bill ${bill.referenceNumber}`}
                        >
                          Discard
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => onResume(bill.id)}
                          aria-label={`Resume bill ${bill.referenceNumber}`}
                        >
                          Resume
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Drawer>
  );
}
